// Apollo client
import { useEffect, useState } from 'react';

// Material ui components
import { ArrowBack } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Paper } from '@mui/material';
import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getFiagroResumeData } from 'src/services/operation/operationFiagro';
import themev5 from 'src/themev5';
import { FiagroResumeData } from 'src/types/operations/fiagro';

import OperationFIAGROAsset from './OperationFIAGROAsset';
import OperationFIAGROLiability from './OperationFIAGROLiability';
import OperationFIAGROResume from './OperationFIAGROResume';
import OperationFIAGROServiceProvider from './OperationFIAGROServiceProvider';
import { If } from '../../../components/If';
import OperationSubheader from '../../../components/OperationSubheader';
import MetaTags from '../../../components/Seo/MetaTags';
// Global components
import { TabSection } from '../../../components/TabSection';
import OperationRelatedArticles from '../OperationRelatedArticles';
import { OperationTimeline } from '../OperationTimeline';
// Local components

export default function OperationFIAGRO() {
  const { tab, hashId, market, slug } = useParams<{
    tab: string;
    hashId: string;
    market: string;
    slug: string;
  }>();
  const history = useHistory();
  const [selectedTab, setSelectedTab] = useState(
    parseInt(window.localStorage.getItem('operationTabIndex') || '0') ||
      'resumo',
  );
  const [error, setError] = useState<Error | null>(null);
  const tabs = [
    { label: 'Resumo', value: 'resumo' },
    { label: 'Prestadores de Serviço', value: 'prestadores' },
    { label: 'Ativo', value: 'ativo' },
    { label: 'Passivo', value: 'passivo' },
    { label: 'Linha do Tempo', value: 'timeline' },
    { label: 'Artigos Relacionados', value: 'artigos' },
  ];

  const handleTabChange = (tabIndex: string) => {
    setSelectedTab(tabIndex);
    if (tabIndex === 'resumo') {
      history.push(`/operacao/${market}/${slug}/${hashId}`);
    } else {
      history.push(`/operacao/${market}/${slug}/${hashId}/${tabIndex}`);
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['fiagroResume', hashId],
    queryFn: () => getFiagroResumeData(hashId) as unknown as FiagroResumeData,
    enabled: !!hashId,
    onError: (error: AxiosError) => {
      setError(
        new Error(
          (error.response?.data as { error: { message?: string } })?.error
            ?.message ?? error.message,
        ),
      );

      toast.error(
        `Erro ao carregar dados do fundo FIAGRO: ${(error?.response?.data as { error: { message?: string } })?.error?.message}`,
      );
    },
  });

  useEffect(() => {
    window.localStorage.setItem(
      'operationTabIndex',
      JSON.stringify(selectedTab),
    );
    return function clean() {
      localStorage.removeItem('operationTabIndex');
    };
  }, [selectedTab]);

  useEffect(() => {
    setSelectedTab(tab ?? 'resumo');
  }, []);

  if (isLoading) return <CircularProgress color="primary" />;

  if (error)
    return (
      <Box
        p={1}
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh">
        <Alert
          severity="error"
          sx={{ fontWeight: 'bold', color: themev5.palette.error.main }}>
          Erro ao carregar dados de Fiagro: {(error as Error).message}
        </Alert>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => history.goBack()}
          startIcon={<ArrowBack />}>
          Voltar
        </Button>
      </Box>
    );

  return (
    <>
      <MetaTags
        title={`${data?.general?.nomeFantasiaFundo} - FIAGRO`}
        description={`Encontre as principais informações sobre ${data?.general?.nomeFantasiaFundo}`}
      />
      <OperationSubheader
        title={data?.general?.nomeFantasiaFundo}
        market={market.toUpperCase()}
        breadcrumbs1="Fundos"
        breadcrumbs2={`${data?.general?.nomeFantasiaFundo} ${market.toUpperCase()}`}
        disableAlerts={true}
        entityId={data?.general?.operationId}
      />
      <Box py={2}>
        <TabSection
          tabList={tabs}
          value={selectedTab}
          setValue={handleTabChange}
          isProfile={false}>
          <Paper square>
            <If condition={selectedTab === 'resumo'}>
              <OperationFIAGROResume
                data={data ?? ({} as unknown as FiagroResumeData)}
                error={error ?? undefined}
              />
            </If>
            <If condition={selectedTab === 'prestadores'}>
              <OperationFIAGROServiceProvider hash={hashId} />
            </If>
            <If condition={selectedTab === 'ativo'}>
              <OperationFIAGROAsset hash={hashId} />
            </If>
            <If condition={selectedTab === 'passivo'}>
              <OperationFIAGROLiability hash={hashId} />
            </If>
            <If condition={selectedTab === 'timeline'}>
              <OperationTimeline hashId={hashId} />
            </If>
            <If condition={selectedTab === 'artigos'}>
              <OperationRelatedArticles hash={hashId} />
            </If>
          </Paper>
        </TabSection>
      </Box>
    </>
  );
}
